import React, { useState, useEffect, useRef } from 'react'
import classNames from 'classnames'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import { Toast } from 'primereact/toast'
import { Button } from 'primereact/button'
import { Toolbar } from 'primereact/toolbar'
import { Dialog } from 'primereact/dialog'
import { InputText } from 'primereact/inputtext'
import { Dropdown } from 'primereact/dropdown'
import { Skeleton } from 'primereact/skeleton';
import { MultiSelect } from 'primereact/multiselect';
import { IconField } from 'primereact/iconfield'
import { InputIcon } from 'primereact/inputicon'
import * as R from 'ramda'
import { formatUTCDate } from '../utils/format'
import { listAllCompaniesRequest, listAllUiDisplyedGroupsRequest, listAllUsersRequest, createUserRequest, editUserRequest } from '../apis/calyxSaaSDashboardAPI'
import { DEFAULT_USER_PREFERENCE_OPTION, USER_PREFERENCE_LANGUAGE_OPTIONS } from '../constants/userPreferenceOptions'
import {
  useQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import axios from 'axios'
import { StatusCodes } from 'http-status-codes';
import { useStore } from '../stores/GlobalStore'
import * as validate from 'validate.js'

export const CrudUser = () => {
  const emptyUser = {
    id: null,
    email: '',
    firstname: '',
    lastname: '',
    lineId: '',
    address: '',
    mobile: '',
    rawPassword: '',
    userPreferenceOption: DEFAULT_USER_PREFERENCE_OPTION,
    companyId: null,
    uiDisplyedGroup: "Super Admin",
    iamResourceBasedPolicy: {
      allow: {
        businessIds: [],
        farmIds: []
      }
    }
  }

  const queryClient = useQueryClient()
  const { state, dispatch } = useStore()
  const [companyMap, setCompanyMap] = useState(null)
  const [companyDropdown, setCompanyDropdown] = useState(null)
  const [createOrEditUserDialogVisibility, setCreateOrEditUserDialogVisibility] = useState(false)
  const [userDetailDialogVisibility, setUserDetailDialogVisibility] = useState(false)
  const [focusedUser, setFocusedUser] = useState(emptyUser)
  const [selectedUsers, setSelectedUsers] = useState(null)
  const [submitted, setSubmitted] = useState(false)
  const [globalFilter, setGlobalFilter] = useState(null)
  const [isVilidNewUsersEmail, setIsVilidNewUsersEmail] = useState(false)
  const [isCreateUserMode, setIsCreateUserMode] = useState(false) // 用於判斷是要建立新使用者還是編輯使用者
  const [focusedUserAvailableBusinessOptions, setFocusedUserAvailableBusinessOptions] = useState([])
  const [selectedBusinessOption, setSelectedBusinessOption] = useState([])
  const toast = useRef(null)
  const dt = useRef(null)

  /**
   * update business multi-select dropdown for focused user
   */
  useEffect(() => {
    const companyId = focusedUser?.companyId
    if (companyId) {
      const currentAllAvailableBusinesses = companyMap[companyId]?.businesses
      const avalableBusinessOptionsForFocusedUser = currentAllAvailableBusinesses.map(business => {
        return {
          type: business.type,
          name: business.name != null ? `${business.type} (${business.name})` : business.type,
          value: business.id
        }
      })
      setFocusedUserAvailableBusinessOptions(avalableBusinessOptionsForFocusedUser)
      setSelectedBusinessOption(focusedUser?.iamResourceBasedPolicy?.allow?.businessIds || [])
    }
  }, [focusedUser])

  const allCompaniesQuery = useQuery(['list', 'companies'], () => {
    return listAllCompaniesRequest()
  }, {
    refetchOnWindowFocus: false,
    retry: 0,
    onSuccess: (companies) => {
      const companyMap = companies.reduce((acc, c) => ({ ...acc, [c.id]: c }), {})
      setCompanyMap(companyMap)
      setCompanyDropdown(Object.keys(companyMap).map(cid => ({ label: companyMap[cid].name, value: companyMap[cid].id })))
    },
    onError: (err) => {
      if (axios.isAxiosError(err)) {
        if (err?.response?.status === StatusCodes.UNAUTHORIZED || err?.response?.status === StatusCodes.FORBIDDEN) {
          let countDownSecond = 3;
          toast.current.show({ severity: 'error', summary: `Session expired, going to logout in ${countDownSecond} seconds... Please login again`, life: 3000 });
          setInterval(() => {
            countDownSecond--;
            if (countDownSecond <= 0) {
              dispatch({
                type: 'LOGOUT',
              })
            }
          }, 1000);
        } else if (err?.response?.status === StatusCodes.INTERNAL_SERVER_ERROR) {
          toast.current.show({ severity: 'error', summary: 'Internal server error, please try again later', life: 5000 });
        } else {
          toast.current.show({ severity: 'error', summary: `Unkown Error, status code: ${err?.response?.status}`, life: 5000 });
        }
      } else {
        toast.current.show({ severity: 'error', summary: 'Unkown Error', life: 5000 });
      }
    }
  })

  const allUiDisplyedGroupsQuery = useQuery(['list', 'uiDisplyedGroups'], () => {
    return listAllUiDisplyedGroupsRequest()
  }, {
    refetchOnWindowFocus: false,
    retry: 0,
    onError: (err) => {
      if (axios.isAxiosError(err)) {
        if (err?.response?.status === StatusCodes.UNAUTHORIZED || err?.response?.status === StatusCodes.FORBIDDEN) {
          let countDownSecond = 3;
          toast.current.show({ severity: 'error', summary: `Session expired, going to logout in ${countDownSecond} seconds... Please login again`, life: 3000 });
          setInterval(() => {
            countDownSecond--;
            if (countDownSecond <= 0) {
              dispatch({
                type: 'LOGOUT',
              })
            }
          }, 1000);
        } else if (err?.response?.status === StatusCodes.INTERNAL_SERVER_ERROR) {
          toast.current.show({ severity: 'error', summary: 'Internal server error, please try again later', life: 5000 });
        } else {
          toast.current.show({ severity: 'error', summary: `Unkown Error, status code: ${err?.response?.status}`, life: 5000 });
        }
      } else {
        toast.current.show({ severity: 'error', summary: 'Unkown Error', life: 5000 });
      }
    }
  })

  const allUsersQuery = useQuery(['list', 'users'], () => {
    return listAllUsersRequest()
  }, {
    refetchOnWindowFocus: false,
    retry: 0,
    onError: (err) => {
      if (axios.isAxiosError(err)) {
        if (err?.response?.status === StatusCodes.UNAUTHORIZED || err?.response?.status === StatusCodes.FORBIDDEN) {
          let countDownSecond = 3;
          toast.current.show({ severity: 'error', summary: `Session expired, going to logout in ${countDownSecond} seconds... Please login again`, life: 3000 });
          setInterval(() => {
            countDownSecond--;
            if (countDownSecond <= 0) {
              dispatch({
                type: 'LOGOUT',
              })
            }
          }, 1000);
        } else if (err?.response?.status === StatusCodes.INTERNAL_SERVER_ERROR) {
          toast.current.show({ severity: 'error', summary: 'Internal server error, please try again later', life: 5000 });
        } else {
          toast.current.show({ severity: 'error', summary: `Unkown Error, status code: ${err?.response?.status}`, life: 5000 });
        }
      } else {
        toast.current.show({ severity: 'error', summary: 'Unkown Error', life: 5000 });
      }
    }
  })

  const createUserMutation = useMutation(createUserOption => {
    return createUserRequest(createUserOption.companyId, createUserOption.requestBody)
  },
    {
      onSuccess: (savedNewUser) => {
        toast.current.show({ severity: 'success', summary: 'Success', detail: 'New user has been saved', life: 3000 })
        queryClient.setQueryData(['list', 'users'], staleUsers => {
          staleUsers.unshift(savedNewUser)
        })
        hideCreateOrEditUserDialog()
      },
      onError: (err) => {
        if (axios.isAxiosError(err)) {
          if (err?.response?.status === StatusCodes.UNAUTHORIZED || err?.response?.status === StatusCodes.FORBIDDEN) {
            let countDownSecond = 7;
            toast.current.show({ severity: 'error', summary: `Session expired, going to logout in ${countDownSecond} seconds... Please login again`, life: 7000 });
            setInterval(() => {
              countDownSecond--;
              if (countDownSecond <= 0) {
                dispatch({
                  type: 'LOGOUT',
                })
              }
            }, 1000);
          } else if (err?.response?.status === StatusCodes.BAD_REQUEST) {
            toast.current.show({ severity: 'error', summary: err.response?.data?.message, life: 5000 });
          } else if (err?.response?.status === StatusCodes.INTERNAL_SERVER_ERROR) {
            toast.current.show({ severity: 'error', summary: 'Internal server error, please try again later', life: 5000 });
          } else {
            toast.current.show({ severity: 'error', summary: `Unkown Error, status code: ${err?.response?.status}`, life: 5000 });
          }
        } else {
          toast.current.show({ severity: 'error', summary: 'Unkown Error', life: 5000 });
        }
      }
    })

  const editUserMutation = useMutation(createUserOption => {
    return editUserRequest(createUserOption.companyId, createUserOption.userId, createUserOption.requestBody)
  },
    {
      onSuccess: (updatedUser) => {
        toast.current.show({ severity: 'success', summary: 'Success', detail: `User [${updatedUser?.firstname} ${updatedUser?.lastname}] has been updated`, life: 3000 })
        queryClient.setQueryData(['list', 'users'], staleUsers => {
          const updateedUserIdx = staleUsers.findIndex(staleUser => staleUser.id === updatedUser.id)
          if (updateedUserIdx >= 0) {
            staleUsers[updateedUserIdx] = updatedUser
          } else {
            toast.current.show({ severity: 'error', summary: 'Something went wrong, please try again later', life: 5000 });
          }
        })
        hideCreateOrEditUserDialog()
      },
      onError: (err) => {
        if (axios.isAxiosError(err)) {
          if (err?.response?.status === StatusCodes.UNAUTHORIZED || err?.response?.status === StatusCodes.FORBIDDEN) {
            let countDownSecond = 3;
            toast.current.show({ severity: 'error', summary: `Session expired, going to logout in ${countDownSecond} seconds... Please login again`, life: 3000 });
            setInterval(() => {
              countDownSecond--;
              if (countDownSecond <= 0) {
                dispatch({
                  type: 'LOGOUT',
                })
              }
            }, 1000);
          } else if (err?.response?.status === StatusCodes.INTERNAL_SERVER_ERROR) {
            toast.current.show({ severity: 'error', summary: 'Internal server error, please try again later', life: 5000 });
          } else {
            toast.current.show({ severity: 'error', summary: `Unkown Error, status code: ${err?.response?.status}`, life: 5000 });
          }
        } else {
          toast.current.show({ severity: 'error', summary: 'Unkown Error', life: 5000 });
        }
      }
    })

  const validateFocusedUser = (focusedUser) => {
    const userConstraints = {
      email: {
        email: true
      }
    };
    if (
      focusedUser?.companyId &&
      focusedUser?.email &&
      focusedUser?.firstname &&
      focusedUser?.lastname &&
      focusedUser?.rawPassword &&
      focusedUser?.mobile &&
      focusedUser?.uiDisplyedGroup &&
      focusedUser?.userPreferenceOption?.language
    ) {
      if (validate(focusedUser, userConstraints)) {
        toast.current.show({ severity: 'error', summary: `Please use valid email format, current email: ${focusedUser.email} is not valid.`, life: 5000 });
        return false
      }
      return true
    } else {
      return false
    }
  }

  const openCreateUserDialog = () => {
    setIsCreateUserMode(true)
    setFocusedUser(emptyUser)
    setCreateOrEditUserDialogVisibility(true)
  }

  const hideCreateOrEditUserDialog = () => {
    setIsCreateUserMode(false)
    setCreateOrEditUserDialogVisibility(false)
  }

  const hideUserDetailDialog = () => {
    setFocusedUser(emptyUser)
    setUserDetailDialogVisibility(false)
  }

  const saveUser = () => {
    if (isCreateUserMode) {
      const isValidNewUser = validateFocusedUser(focusedUser) // 若為合法格式, 會回傳undefined

      if (!isValidNewUser) {
        toast.current.show({ severity: 'error', summary: 'Please fill in required fields before apply.', life: 3000 })
        return
      }
      createUserMutation.mutate({
        companyId: focusedUser.companyId,
        requestBody: {
          ...focusedUser
        }
      })
    } else {
      editUserMutation.mutate({
        companyId: focusedUser.originalCompanyId,
        userId: focusedUser.id,
        requestBody: {
          ...focusedUser
        }
      })
    }
  }

  const openEditUserDialog = (user) => {
    const newFocuedUser = { ...user }
    newFocuedUser.originalCompanyId = newFocuedUser.companyId
    setFocusedUser(newFocuedUser)
    setCreateOrEditUserDialogVisibility(true)
  }

  const showUserDetail = (user) => {
    setFocusedUser({ ...user })
    setUserDetailDialogVisibility(true)
  }

  const exportCSV = () => {
    dt.current.exportCSV()
  }

  const onInputChange = (e, name) => {
    const val = (e.target && e.target.value) || emptyUser[`${name}`]
    if (name === 'email') {
      const tmpUserForCheck = {
        email: val
      }
      const userConstraints = {
        email: {
          email: true
        }
      };
      if (validate(tmpUserForCheck, userConstraints)) {
        setIsVilidNewUsersEmail(false)
      } else {
        setIsVilidNewUsersEmail(true)
      }
    }

    const _user = { ...focusedUser }
    _user[`${name}`] = val

    setFocusedUser(_user)
  }

  const onInputCompanyChange = (e) => {
    const companyId = (e.target && e.target.value)
    const newFocusedUser = { ...focusedUser }
    newFocusedUser.companyId = companyId

    // 創建新使用者時，使用 company config 上的 defaultUserPreferenceOption 作為預設設定
    if (isCreateUserMode) {
      const company = companyMap?.[companyId]

      newFocusedUser.userPreferenceOption = {
        ...DEFAULT_USER_PREFERENCE_OPTION,
        ...company?.defaultUserPreferenceOption,
      }
    }

    setFocusedUser(newFocusedUser)
  }

  const onInputLanguageChange = (e) => {
    const val = (e.target && e.target.value)
    const _user = { ...focusedUser }
    if (!_user.userPreferenceOption) _user.userPreferenceOption = {}
    _user.userPreferenceOption.language = val

    setFocusedUser(_user)
  }

  // const onInputLineIdChange = (e) => {
  //   const val = (e.target && e.target.value)
  //   const _user = { ...focusedUser }
  //   if (!_user.messagingPlatformOption) _user.messagingPlatformOption = {}
  //   _user.messagingPlatformOption.lineId = val

  //   setFocusedUser(_user)
  // }

  // const onInputTelegramIdChange = (e) => {
  //   const val = (e.target && e.target.value)
  //   const _user = { ...focusedUser }
  //   if (!_user.messagingPlatformOption) _user.messagingPlatformOption = {}
  //   _user.messagingPlatformOption.telegramId = val

  //   setFocusedUser(_user)
  // }

  const leftToolbarTemplate = () => {
    return (
      <>
        <div className='my-2'>
          <Button label='New' icon='pi pi-plus' className='p-button-success mr-2' data-test-id='createNewUser' onClick={openCreateUserDialog} />
        </div>
      </>
    )
  }

  const rightToolbarTemplate = () => {
    return (
      <>
        <Button label='Export' icon='pi pi-upload' className='p-button-help' onClick={exportCSV} />
      </>
    )
  }

  const idBodyTemplate = (rowData) => {
    return (
      <>
        <span className='p-column-title'>Id</span>
        {rowData.id}
      </>
    )
  }
  const nameBodyTemplate = (rowData) => {
    return (
      <>
        <span className='p-column-title'>Name</span>
        {rowData.firstname + ' ' + rowData.lastname}
      </>
    )
  }

  const roleBodyTemplate = (rowData) => {
    return (
      <>
        <span className='p-column-title'>Role</span>
        {rowData?.uiDisplyedGroup}
      </>
    )
  }

  const emailBodyTemplate = (rowData) => {
    return (
      <>
        <span className='p-column-title'>Email</span>
        {rowData.email}
      </>
    )
  }

  const companyBodyTemplate = (rowData) => {
    return (
      <>
        <span className='p-column-title'>Company</span>
        {companyMap?.[rowData?.companyId] ? companyMap[rowData.companyId].name : ''}
      </>
    )
  }

  const actionBodyTemplate = (rowData) => {
    return (
      <div className='actions'>
        <Button icon='pi pi-info-circle' className='p-button-rounded p-button-info mr-2' data-test-id='showUserDetail' onClick={() => showUserDetail(rowData)} />
        <Button icon='pi pi-pencil' className='p-button-rounded p-button-success mr-2' onClick={() => openEditUserDialog(rowData)} />
      </div>
    )
  }

  const header = (
    <div className='flex flex-column md:flex-row md:justify-content-between md:align-items-center'>
      <h5 className='m-0'>Manage Users</h5>
      <IconField iconPosition="left" className='block mt-2 md:mt-0'>
        <InputIcon className='pi pi-search' />
        <InputText type='search' data-test-id='searchUser' onInput={(e) => setGlobalFilter(e.target.value)} placeholder='Search...' />
      </IconField>
    </div>
  )

  const userDialogFooter = (
    <>
      <Button label='Cancel' icon='pi pi-times' className='p-button-text' onClick={hideCreateOrEditUserDialog} />
      <Button label='Save' icon='pi pi-check' className='p-button-text' data-test-id='savelNewUser' onClick={saveUser} />
    </>
  )

  const parseDate = (unixTime) => {
    if (R.isNil(unixTime)) return ''
    return formatUTCDate(new Date(unixTime))
  }

  /**
   *
   * @param {string[]} businesses
   */
  const onSelectBusiness = (businesses) => {
    setSelectedBusinessOption(businesses)
    const newFocusedUser = { ...focusedUser }
    newFocusedUser.iamResourceBasedPolicy.allow.businessIds = businesses;
    setFocusedUser(newFocusedUser)
  }

  return (
    <div className='grid crud-demo'>
      <div className='col-12'>
        <div className='card'>
          <Toast ref={toast} />
          <Toolbar className='mb-4' left={leftToolbarTemplate} right={rightToolbarTemplate} />

          {allCompaniesQuery.isFetching || allCompaniesQuery.isError || allUsersQuery.isFetching || allUsersQuery.isError ? (
            <DataTable value={Array.from({ length: 10 })} className="p-datatable-striped company-table-skeleton" header={header}>
              <Column field="id" header="id" style={{ width: '16.66%', minWidth: '10rem' }} body={<Skeleton />}></Column>
              <Column field="name" header="Name" style={{ width: '16.66%', minWidth: '10rem' }} body={<Skeleton />}></Column>
              <Column field="email" header="Email" style={{ width: '16.66%', minWidth: '10rem' }} body={<Skeleton />}></Column>
              <Column field="company" header="Company" style={{ width: '16.66%', minWidth: '10rem' }} body={<Skeleton />}></Column>
              <Column field="role" header="Role" style={{ width: '16.66%', minWidth: '10rem' }} body={<Skeleton />}></Column>
              <Column body={<Skeleton />}></Column>
            </DataTable>
          ) : (
            <DataTable
              ref={dt} value={allUsersQuery?.data} selection={selectedUsers} onSelectionChange={(e) => setSelectedUsers(e.value)}
              dataKey='id' paginator rows={50} rowsPerPageOptions={[5, 10, 25, 50, 100]} className='datatable-responsive'
              paginatorTemplate='FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown'
              currentPageReportTemplate='Showing {first} to {last} of {totalRecords} users'
              globalFilter={globalFilter}
              globalFilterFields={["firstname", "lastname", "email", "company", "uiDisplyedGroup", "id"]} // 解決first name + last name 搜尋問題
              emptyMessage='No users found.' header={header} responsiveLayout='scroll'
            >
              <Column field='id' header='id' sortable body={idBodyTemplate} headerStyle={{ width: '16.66%', minWidth: '10rem' }} />
              <Column field='name' header='Name' body={nameBodyTemplate} headerStyle={{ width: '16.66%', minWidth: '10rem' }} />
              <Column field='email' header='Email' body={emailBodyTemplate} headerStyle={{ width: '16.66%', minWidth: '10rem' }} />
              <Column field='company' header='Company' body={companyBodyTemplate} headerStyle={{ width: '16.66%', minWidth: '10rem' }} />
              <Column field='uiDisplyedGroup' header='Role' body={roleBodyTemplate} headerStyle={{ width: '16.66%', minWidth: '10rem' }} />
              <Column body={actionBodyTemplate} />
            </DataTable>
          )}

          <Dialog blockScroll={true} visible={createOrEditUserDialogVisibility} style={{ width: '450px', }} header={isCreateUserMode ? 'Create User' : 'Edit User'} modal className='p-fluid' footer={userDialogFooter} onHide={hideCreateOrEditUserDialog}>
            <div className='field'>
              <label htmlFor='email'>Email</label>
              <InputText id='email' data-test-id='typeUserEmail' value={focusedUser.email} onChange={(e) => onInputChange(e, 'email')} disabled={!isCreateUserMode || createUserMutation.isLoading || editUserMutation.isLoading} required autoFocus />
              {(!focusedUser.email || !isVilidNewUsersEmail) && isCreateUserMode && <small className='p-invalid' style={{ color: 'red' }}>{isVilidNewUsersEmail ? 'Email is required.' : 'Email format is not valid.'}</small>}
            </div>
            <div className='field'>
              <label htmlFor='firstname'>First Name</label>
              <InputText id='firstname' data-test-id='typeUserFirstname' value={focusedUser.firstname} onChange={(e) => onInputChange(e, 'firstname')} required autoFocus disabled={createUserMutation.isLoading || editUserMutation.isLoading} />
              {!focusedUser.firstname && <small className='p-invalid' style={{ color: 'red' }}>Firstname is required.</small>}
            </div>
            <div className='field'>
              <label htmlFor='lastname'>Last Name</label>
              <InputText id='lastname' data-test-id='typeUserLastname' value={focusedUser.lastname} onChange={(e) => onInputChange(e, 'lastname')} required autoFocus disabled={createUserMutation.isLoading || editUserMutation.isLoading} />
              {!focusedUser.lastname && <small className='p-invalid' style={{ color: 'red' }}>Lastname is required.</small>}
            </div>
            <div className='field' data-test-id='chooseUserCompany'>
              <label htmlFor='company'>Company</label>
              <Dropdown
                id='companyId' value={focusedUser?.companyId}
                disabled={createUserMutation.isLoading || editUserMutation.isLoading}
                options={companyDropdown}
                filter
                onChange={(e) => onInputCompanyChange(e)} required autoFocus
                className={classNames({ 'p-invalid': submitted && !focusedUser?.companyId })}
              />
              {!focusedUser?.companyId && <small className='p-invalid' style={{ color: 'red' }}>Company is required.</small>}
            </div>
            <div className='field' data-test-id='chooseRole'>
              <label htmlFor='uiDisplyedGroup'>Role</label>
              <Dropdown
                id='uiDisplyedGroup' value={focusedUser.uiDisplyedGroup}
                disabled={createUserMutation.isLoading || editUserMutation.isLoading}
                options={allUiDisplyedGroupsQuery?.data}
                onChange={(e) => onInputChange(e, 'uiDisplyedGroup')} required autoFocus
                className={classNames({ 'p-invalid': submitted && !R.isNil(focusedUser.uiDisplyedGroup) })}
              />
              {!focusedUser?.uiDisplyedGroup && <small className='p-invalid' style={{ color: 'red' }}>role is required.</small>}
            </div>
            <div className='field' data-test-id='chooseBusiness'>
              <label htmlFor='businessIds'>Own Business</label>
              <MultiSelect value={selectedBusinessOption || []} options={focusedUserAvailableBusinessOptions} onChange={(e) => onSelectBusiness(e.value)} display="chip" optionLabel="name" placeholder="Select businesses for user" />
              {!focusedUser?.uiDisplyedGroup && <small className='p-invalid' style={{ color: 'red' }}>role is required.</small>}
            </div>
            <div className='field' data-test-id='chooseLanguage'>
              <label htmlFor='language'>Language</label>
              <Dropdown
                id='language' value={focusedUser?.userPreferenceOption?.language}
                disabled={createUserMutation.isLoading || editUserMutation.isLoading}
                options={USER_PREFERENCE_LANGUAGE_OPTIONS}
                onChange={(e) => onInputLanguageChange(e)} required autoFocus
                className={classNames({ 'p-invalid': submitted && !focusedUser?.userPreferenceOption?.language })}
              />
            </div>

            {/* 後續有需要再開 */}
            {/* {isCreateUserMode ? null : (
              <>
                <div className='field'>
                  <label htmlFor='lineId'>Line Id</label>
                  <InputText
                    id='lineId' value={focusedUser?.messagingPlatformOption?.lineId || ''} onChange={(e) => onInputLineIdChange(e)} autoFocus
                    className={classNames({ 'p-invalid': submitted && !focusedUser?.messagingPlatformOption?.lineId })}
                  />
                </div>
                <div className='field'>
                  <label htmlFor='telegramId'>Telegram Id</label>
                  <InputText
                    id='telegramId' value={focusedUser?.messagingPlatformOption?.telegramId || ''} onChange={(e) => onInputTelegramIdChange(e)} autoFocus
                    disabled={createUserMutation.isLoading || editUserMutation.isLoading}
                  />
                </div>
              </>
            )} */}

             <div className='field'>
              <label htmlFor='address'>Address</label>
              <InputText
                id='address' value={focusedUser.address} onChange={(e) => onInputChange(e, 'address')} required autoFocus
                disabled={createUserMutation.isLoading || editUserMutation.isLoading}
              />
            </div>

            <div className='field'>
              <label htmlFor='mobile'>Mobile</label>
              <InputText
                id='mobile' value={focusedUser.mobile} onChange={(e) => onInputChange(e, 'mobile')} required autoFocus
                disabled={createUserMutation.isLoading || editUserMutation.isLoading}
              />
              {!focusedUser.mobile && <small className='p-invalid' style={{ color: 'red' }}>Mobile is required.</small>}
            </div>
            {R.isNil(focusedUser.id)
              ? (
                <div className='field' data-test-id='typeUserPassword'>
                  <label htmlFor='mobile'>Password</label>
                  <InputText
                    id='rawPassword' value={focusedUser.rawPassword} onChange={(e) => onInputChange(e, 'rawPassword')} autoFocus
                    disabled={createUserMutation.isLoading || editUserMutation.isLoading}
                  />
                  {!focusedUser?.rawPassword && <small className='p-invalid' style={{ color: 'red' }}>Password is required.</small>}
                </div>
              )
              : null}
          </Dialog>

          <Dialog blockScroll={true} visible={userDetailDialogVisibility} style={{ width: '450px' }} header='User Detail' modal className='p-fluid' onHide={hideUserDetailDialog}>
            <div className='field'>
              <label htmlFor='firstname' style={{ color: 'gray', fontSize: '18px', marginRight: '10px' }}>First Name: </label><label>{focusedUser.firstname || <code>empty</code>}</label>
            </div>
            <div className='field'>
              <label htmlFor='lastname' style={{ color: 'gray', fontSize: '18px', marginRight: '10px' }}>Last Name: </label><label>{focusedUser.lastname || <code>empty</code>}</label>
            </div>
            <div className='field'>
              <label htmlFor='email' style={{ color: 'gray', fontSize: '18px', marginRight: '10px' }}>Email: </label><label>{focusedUser.email}</label>
            </div>
            <div className='field'>
              <label htmlFor='company' style={{ color: 'gray', fontSize: '18px', marginRight: '10px' }}>Company: </label>
              <label>{focusedUser?.companyId ? (companyMap?.[focusedUser?.companyId] ? companyMap[focusedUser.companyId].name : '') : ''}</label>
            </div>
            <div className='field'>
              <label htmlFor='bussinesses' style={{ color: 'gray', fontSize: '18px', marginRight: '10px' }}>Own Businesses: </label>
              <ul>
                {focusedUser.iamResourceBasedPolicy.allow.businessIds.map(businessId => {
                  const business = companyMap[focusedUser.companyId]?.businesses?.find(businesses => businesses.id === businessId)
                  return (
                    business != null
                      ? <li key={businessId}>
                          {business.name != null ? `${business.type} (${business.name})` : business.type}
                        </li>
                      : null
                  )
                })}
              </ul>
            </div>
            <div className='field'>
              <label htmlFor='language' style={{ color: 'gray', fontSize: '18px', marginRight: '10px' }}>Language: </label>
              <label>{focusedUser.userPreferenceOption.language}</label>
            </div>
            <div className='field' data-test-id='checkUserRole'>
              <label htmlFor='role' style={{ color: 'gray', fontSize: '18px', marginRight: '10px' }}>Role: </label><label>{focusedUser.uiDisplyedGroup}</label>
            </div>
            <div className='field'>
              <label htmlFor='address' style={{ color: 'gray', fontSize: '18px', marginRight: '10px' }}>Address: </label><label>{focusedUser.address || <code>empty</code>}</label>
            </div>
            <div className='field'>
              <label htmlFor='mobile' style={{ color: 'gray', fontSize: '18px', marginRight: '10px' }}>Mobile: </label><label>{focusedUser.mobile || <code>empty</code>}</label>
            </div>
            <div className='field'>
              <label htmlFor='lineid' style={{ color: 'gray', fontSize: '18px', marginRight: '10px' }}>Line Id: </label><label>{focusedUser?.messagingPlatformOption?.lineId || <code>empty</code>}</label>
            </div>
            <div className='field'>
              <label htmlFor='telegramId' style={{ color: 'gray', fontSize: '18px', marginRight: '10px' }}>Telegram Id: </label><label>{focusedUser?.messagingPlatformOption?.telegramId || <code>empty</code>}</label>
            </div>
          </Dialog>
        </div>
      </div>
    </div>
  )
}
